import { isBrowserRenderable } from '@atlas/types'
import { useEffect, useState } from 'react'

/**
 * A local preview for a file that has not finished uploading, so the tile shows
 * the user's own bytes immediately rather than waiting on the server-generated
 * thumbnail (which only exists after `thumbnail_generation` completes).
 *
 * `null` means "no preview available" — the caller renders the file-type icon.
 * That covers both "this type never previews" and "we tried and the browser
 * couldn't decode it", deliberately: from the tile's point of view they render
 * identically, and collapsing them keeps the caller from branching on a
 * distinction it doesn't act on.
 */
export function useFilePreview(file: File | undefined, contentType: string): string | null {
	const [previewUrl, setPreviewUrl] = useState<string | null>(null)

	useEffect(() => {
		if (!file) {
			setPreviewUrl(null)
			return
		}

		// `isBrowserRenderable`, not `startsWith('image/')`: PSD, TIFF and HEIC all
		// carry an `image/` type and none of them decode in an `<img>`, so the
		// prefix check produced an object URL that rendered as a broken-image icon.
		// Falling through to the type marker is correct until the server's own
		// thumbnail lands — sharp handles those formats, the browser does not.
		if (isBrowserRenderable(contentType)) {
			const url = URL.createObjectURL(file)
			setPreviewUrl(url)
			// Revoking on every change (not just unmount) matters here: a queue can
			// re-render with a different file in the same tile slot, and an
			// un-revoked blob URL pins the whole file in memory for the session.
			return () => URL.revokeObjectURL(url)
		}

		if (contentType.startsWith('video/')) {
			return captureVideoFirstFrame(file, setPreviewUrl)
		}

		setPreviewUrl(null)
		return
	}, [file, contentType])

	return previewUrl
}

/**
 * Draws a video's first frame to a canvas and hands back a data URL.
 *
 * Returns its own cleanup so the source blob URL is revoked and the detached
 * `<video>` is torn down even if the seek never completes — an undecodable
 * codec fires `error` on some browsers and simply never fires `seeked` on
 * others, so cleanup cannot be hung off the success path.
 */
function captureVideoFirstFrame(file: File, onFrame: (dataUrl: string | null) => void): () => void {
	const objectUrl = URL.createObjectURL(file)
	const video = document.createElement('video')
	let cancelled = false

	const cleanup = () => {
		cancelled = true
		// Detaching the source is enough to let the element be collected; calling
		// `load()` to force it isn't, because the element is never in the document
		// and some environments don't implement it at all.
		video.removeAttribute('src')
		URL.revokeObjectURL(objectUrl)
	}

	video.muted = true
	video.preload = 'metadata'
	video.src = objectUrl

	video.addEventListener('loadeddata', () => {
		if (cancelled) return
		const canvas = document.createElement('canvas')
		canvas.width = video.videoWidth
		canvas.height = video.videoHeight
		const context = canvas.getContext('2d')
		if (!context || canvas.width === 0 || canvas.height === 0) {
			onFrame(null)
			return
		}
		context.drawImage(video, 0, 0, canvas.width, canvas.height)
		onFrame(canvas.toDataURL('image/png'))
	})

	// Decode failure falls back to the file-type icon rather than an empty box.
	video.addEventListener('error', () => {
		if (!cancelled) onFrame(null)
	})

	return cleanup
}
